import React, { useEffect, useState } from 'react';
import { getDoctors, getAvailableSlots, bookAppointment, getPatientByPhone, registerPatient } from '../api';
import { formatDate, formatTime, formatTimeRange } from '../utils/format';
import styles from './Page.module.css';

const STEPS = { LOOKUP: 1, REGISTER: 2, BOOK: 3 };

export default function BookAppointmentPage() {
  const [doctors, setDoctors] = useState([]);
  const [step, setStep] = useState(STEPS.LOOKUP);

  const [phone, setPhone] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupMsg, setLookupMsg] = useState('');

  const [regForm, setRegForm] = useState({ name: '', age: '', gender: '', email: '' });
  const [regLoading, setRegLoading] = useState(false);

  const [patient, setPatient] = useState(null);

  const [doctorId, setDoctorId] = useState('');
  const [date, setDate] = useState('');
  const [slots, setSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [noSlots, setNoSlots] = useState(false);
  const [bookLoading, setBookLoading] = useState(false);
  const [bookMsg, setBookMsg] = useState('');
  const [bookMsgType, setBookMsgType] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const maxDate = new Date();
  maxDate.setDate(maxDate.getDate() + 60);
  const maxDateStr = maxDate.toISOString().split('T')[0];

  useEffect(() => { getDoctors().then(r => setDoctors(r.data)); }, []);

  const handleLookup = async (e) => {
    e.preventDefault();
    if (phone.length !== 10) {
      setLookupMsg('Please enter a valid 10-digit phone number.');
      return;
    }
    setLookupLoading(true);
    setLookupMsg('');
    try {
      const res = await getPatientByPhone(phone);
      setPatient(res.data);
      setLookupMsg('Returning patient found: ' + res.data.name);
      setTimeout(() => setStep(STEPS.BOOK), 800);
    } catch {
      setLookupMsg('Patient not found. Please register below.');
      setStep(STEPS.REGISTER);
    }
    setLookupLoading(false);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setRegLoading(true);
    try {
      const res = await registerPatient({
        name: regForm.name,
        phone,
        age: regForm.age,
        gender: regForm.gender,
        email: regForm.email,
      });
      setPatient(res.data);
      setStep(STEPS.BOOK);
    } catch {
      alert('Registration failed. Please try again.');
    }
    setRegLoading(false);
  };

  const handleFetchSlots = async () => {
    if (!doctorId || !date) return;
    setSlotsLoading(true);
    setSlots([]);
    setSelectedSlot(null);
    setNoSlots(false);
    setBookMsg('');
    try {
      const res = await getAvailableSlots(parseInt(doctorId), date);
      if (res.data.length === 0) setNoSlots(true);
      else setSlots(res.data);
    } catch { setNoSlots(true); }
    setSlotsLoading(false);
  };

  const handleBook = async (e) => {
    e.preventDefault();
    if (!selectedSlot) {
      setBookMsg('Please select a time slot.');
      setBookMsgType('error');
      return;
    }
    setBookLoading(true);
    setBookMsg('');
    try {
      await bookAppointment({
        appointmentDate: date,
        doctor: { id: parseInt(doctorId) },
        slot: { id: selectedSlot.id },
        patient: { id: patient.id },
      });
      setBookMsgType('success');
      setBookMsg(
        'Appointment booked for ' + patient.name +
        ' on ' + formatDate(date) +
        ' at ' + formatTime(selectedSlot.startTime)
      );
      setSelectedSlot(null);
      setDoctorId('');
      setDate('');
      setSlots([]);
      setTimeout(() => {
        setStep(STEPS.LOOKUP);
        setPhone('');
        setPatient(null);
        setLookupMsg('');
        setBookMsg('');
      }, 3000);
    } catch {
      setBookMsgType('error');
      setBookMsg('Failed to book. Please try again.');
    }
    setBookLoading(false);
  };

  const resetAll = () => {
    setStep(STEPS.LOOKUP);
    setPhone('');
    setPatient(null);
    setLookupMsg('');
    setRegForm({ name: '', age: '', gender: '', email: '' });
    setDoctorId('');
    setDate('');
    setSlots([]);
    setSelectedSlot(null);
    setBookMsg('');
  };

  return (
    <div>
      <h2 className={styles.heading}>Book Appointment</h2>

      {/* Step Indicator */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 24, alignItems: 'center' }}>
        {['1. Patient Lookup', '2. Register', '3. Book Slot'].map((label, idx) => {
          const stepNum = idx + 1;
          const isActive = step === stepNum;
          const isDone = step > stepNum;
          return (
            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                width: 28, height: 28, borderRadius: '50%',
                background: isDone ? '#2e7d32' : isActive ? '#c0272d' : '#e0e0e0',
                color: isDone || isActive ? '#fff' : '#999',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700, fontSize: 13,
              }}>
                {isDone ? '✓' : stepNum}
              </div>
              <span style={{
                fontWeight: isActive ? 700 : 400,
                color: isActive ? '#c0272d' : isDone ? '#2e7d32' : '#999',
                fontSize: 14,
              }}>
                {label}
              </span>
              {idx < 2 && <span style={{ color: '#ccc', fontSize: 18 }}>{'>'}</span>}
            </div>
          );
        })}
      </div>

      <div className={styles.grid}>

        {/* LEFT PANEL */}
        <div className={styles.card}>

          {/* STEP 1 */}
          {step === STEPS.LOOKUP && (
            <>
              <h3>Patient Lookup</h3>
              <form onSubmit={handleLookup}>
                <div className={styles.field}>
                  <label>Patient Phone Number *</label>
                  <input type="tel" value={phone}
                    placeholder="Enter 10-digit phone"
                    maxLength={10}
                    onChange={e => { setPhone(e.target.value); setLookupMsg(''); }}
                    required />
                </div>
                {lookupMsg && (
                  <p style={{
                    color: lookupMsg.startsWith('Returning') ? '#2e7d32'
                      : lookupMsg.startsWith('Please enter') ? '#b71c1c'
                        : '#1a3a6b',
                    fontWeight: 500, marginBottom: 10, fontSize: 14,
                  }}>
                    {lookupMsg}
                  </p>
                )}
                <button type="submit" className={styles.btn} disabled={lookupLoading}>
                  {lookupLoading ? 'Checking...' : 'Check Patient'}
                </button>
              </form>
            </>
          )}

          {/* STEP 2 */}
          {step === STEPS.REGISTER && (
            <>
              <h3>Register New Patient</h3>
              <p style={{ color: '#1a3a6b', fontWeight: 500, marginBottom: 12, fontSize: 14 }}>
                Phone: {phone}
              </p>
              <form onSubmit={handleRegister}>
                <div className={styles.field}>
                  <label>Full Name *</label>
                  <input type="text" value={regForm.name}
                    placeholder="e.g. Ramesh Kumar"
                    onChange={e => setRegForm({ ...regForm, name: e.target.value })}
                    required />
                </div>
                <div className={styles.row}>
                  <div className={styles.field}>
                    <label>Age *</label>
                    <input type="number" value={regForm.age}
                      placeholder="e.g. 35" min="1" max="120"
                      onChange={e => setRegForm({ ...regForm, age: e.target.value })}
                      required />
                  </div>
                  <div className={styles.field}>
                    <label>Gender *</label>
                    <select value={regForm.gender}
                      onChange={e => setRegForm({ ...regForm, gender: e.target.value })}
                      required>
                      <option value="">Select</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                </div>
                <div className={styles.field}>
                  <label>Email (optional)</label>
                  <input type="email" value={regForm.email}
                    placeholder="e.g. ramesh@email.com"
                    onChange={e => setRegForm({ ...regForm, email: e.target.value })} />
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="submit" className={styles.btn} disabled={regLoading}>
                    {regLoading ? 'Registering...' : 'Register & Continue'}
                  </button>
                  <button type="button" className={styles.btnOutline} onClick={resetAll}>
                    Cancel
                  </button>
                </div>
              </form>
            </>
          )}

          {/* STEP 3 */}
          {step === STEPS.BOOK && patient && (
            <>
              <h3>Select Doctor & Slot</h3>
              <div style={{
                background: '#e8f5e9', borderRadius: 8, padding: '10px 14px',
                marginBottom: 16, display: 'flex',
                justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 700, color: '#2e7d32' }}>
                    {patient.name}
                  </p>
                  <p style={{ margin: 0, fontSize: 13, color: '#555' }}>
                    {patient.phone} | {patient.gender}, {patient.age} yrs
                  </p>
                </div>
                <button className={styles.btnOutline}
                  style={{ fontSize: 12, padding: '4px 10px', width: 'auto' }}
                  onClick={resetAll}>
                  Change
                </button>
              </div>

              <div className={styles.field}>
                <label>Doctor *</label>
                <select value={doctorId}
                  onChange={e => { setDoctorId(e.target.value); setSlots([]); setSelectedSlot(null); }}
                  required>
                  <option value="">Select Doctor</option>
                  {doctors.map(d => (
                    <option key={d.id} value={d.id}>
                      {d.name} - {d.specialization}
                    </option>
                  ))}
                </select>
              </div>

              <div className={styles.field}>
                <label>Appointment Date *</label>
                <input type="date" value={date}
                  min={today} max={maxDateStr}
                  onChange={e => { setDate(e.target.value); setSlots([]); setSelectedSlot(null); }} />
              </div>

              <button className={styles.btn} onClick={handleFetchSlots}
                disabled={!doctorId || !date || slotsLoading}>
                {slotsLoading ? 'Loading Slots...' : 'Get Available Slots'}
              </button>

              {noSlots && (
                <p style={{ marginTop: 14, color: '#b71c1c', fontWeight: 500, fontSize: 14 }}>
                  No available slots for this doctor on selected date.
                </p>
              )}

              {slots.length > 0 && (
                <div style={{ marginTop: 16 }}>
                  <label style={{ fontWeight: 600, display: 'block', marginBottom: 8, color: '#1a3a6b' }}>
                    Select Time Slot *
                  </label>
                  <div className={styles.slotGrid}>
                    {slots.map(slot => (
                      <span key={slot.id} className={styles.slotChip}
                        onClick={() => setSelectedSlot(slot)}
                        style={{
                          cursor: 'pointer',
                          background: selectedSlot && selectedSlot.id === slot.id ? '#c0272d' : '#e8edf6',
                          color: selectedSlot && selectedSlot.id === slot.id ? '#fff' : '#1a3a6b',
                          border: selectedSlot && selectedSlot.id === slot.id
                            ? '2px solid #c0272d' : '2px solid transparent',
                          fontWeight: selectedSlot && selectedSlot.id === slot.id ? 700 : 400,
                          transition: 'all 0.2s',
                        }}>
                        {formatTimeRange(slot.startTime, slot.endTime)}
                      </span>
                    ))}
                  </div>
                  {selectedSlot && (
                    <p style={{ marginTop: 10, color: '#c0272d', fontWeight: 500, fontSize: 14 }}>
                      Selected: {formatTimeRange(selectedSlot.startTime, selectedSlot.endTime)}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>

        {/* RIGHT PANEL */}
        {step === STEPS.BOOK && patient && (
          <div className={styles.card}>
            <h3>Confirm Appointment</h3>
            <form onSubmit={handleBook}>
              <div style={{
                background: '#f5f5f5', borderRadius: 8,
                padding: 16, marginBottom: 16,
              }}>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Patient:</strong> {patient.name}
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Phone:</strong> {patient.phone}
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Age / Gender:</strong> {patient.age} yrs / {patient.gender}
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Doctor:</strong>{' '}
                  {doctors.find(d => d.id === parseInt(doctorId)) ? doctors.find(d => d.id === parseInt(doctorId)).name : '-'}
                </p>
                <p style={{ margin: '0 0 8px' }}>
                  <strong>Date:</strong> {date ? formatDate(date) : '-'}
                </p>
                <p style={{ margin: 0 }}>
                  <strong>Time:</strong>{' '}
                  {selectedSlot ? formatTimeRange(selectedSlot.startTime, selectedSlot.endTime) : '-'}
                </p>
              </div>

              {bookMsg && (
                <p style={{
                  color: bookMsgType === 'success' ? '#2e7d32' : '#b71c1c',
                  fontWeight: 500, marginBottom: 12, fontSize: 14,
                }}>
                  {bookMsg}
                </p>
              )}

              <button type="submit" className={styles.btn} disabled={bookLoading}>
                {bookLoading ? 'Booking...' : 'Confirm & Book Appointment'}
              </button>
            </form>
          </div>
        )}

      </div>
    </div>
  );
}
